<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { listMyCreditLogs, listMyCreditOrders, type CreditLogItem, type MyCreditOrderItem } from '../../api/user/recharge'
import { useI18n } from '../../composables/useI18n'
import DatetimeViewer from '../DatetimeViewer.vue'
import EmptyState from '../EmptyState.vue'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const { t } = useI18n()
const activeTab = ref('orders')
const loadingOrders = ref(false)
const loadingLogs = ref(false)
const orders = ref<MyCreditOrderItem[]>([])
const logs = ref<CreditLogItem[]>([])

const orderEmpty = computed(() => !loadingOrders.value && orders.value.length === 0)
const logEmpty = computed(() => !loadingLogs.value && logs.value.length === 0)

const orderColumns = computed(() => [
  { title: t('shell.account.order.colOrderNo'), dataIndex: 'orderNo' },
  { title: t('shell.account.order.colTitle'), dataIndex: 'title' },
  { title: t('shell.account.order.colPrice'), key: 'price' },
  { title: t('shell.account.order.colRefundAmount'), key: 'refundAmount' },
  { title: t('shell.account.order.colCredit'), dataIndex: 'credit' },
  { title: t('shell.account.order.colStatus'), key: 'status' },
  { title: t('shell.account.order.colCreatedAt'), key: 'createdAt' },
])
const logColumns = computed(() => [
  { title: t('shell.account.order.colChange'), key: 'change' },
  { title: t('shell.account.order.colBalance'), dataIndex: 'balance' },
  { title: t('shell.account.order.colRemark'), dataIndex: 'remark' },
  { title: t('shell.account.order.colCreatedAt'), key: 'createdAt' },
])

function orderStatusLabel(status: number) {
  switch (status) {
    case 2:
      return t('shell.account.order.statusPayed')
    case 3:
      return t('shell.account.order.statusClosed')
    case 4:
      return t('shell.account.order.statusRefunded')
    case 5:
      return t('shell.account.order.statusPartRefunded')
    default:
      return t('shell.account.order.statusNew')
  }
}
function orderStatusColor(status: number) {
  switch (status) {
    case 2:
      return 'green'
    case 3:
      return 'default'
    case 4:
      return 'orange'
    case 5:
      return 'gold'
    default:
      return 'blue'
  }
}

async function loadOrders() {
  loadingOrders.value = true
  try {
    const data = await listMyCreditOrders({ page: 1, pageSize: 50 })
    orders.value = data.records || []
  } finally {
    loadingOrders.value = false
  }
}
async function loadLogs() {
  loadingLogs.value = true
  try {
    const data = await listMyCreditLogs({ page: 1, pageSize: 50 })
    logs.value = data.records || []
  } finally {
    loadingLogs.value = false
  }
}

function handleOpen(value: boolean) {
  emit('update:open', value)
}

watch(
  () => props.open,
  (value) => {
    if (value) {
      loadOrders()
      loadLogs()
    }
  },
  { immediate: true },
)
</script>

<template>
  <a-modal
    width="95vw"
    :open="open"
    :footer="null"
    :keyboard="true"
    :mask-closable="true"
    class="large-modal"
    @cancel="handleOpen(false)"
  >
    <div class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{{ t('shell.account.order.title') }}</div>
    <a-tabs v-model:active-key="activeTab">
      <a-tab-pane key="orders" :tab="t('shell.account.order.tabOrders')">
        <EmptyState v-if="orderEmpty" :description="t('shell.account.order.empty')" />
        <a-table
          v-else
          :columns="orderColumns"
          :data-source="orders"
          :loading="loadingOrders"
          :pagination="false"
          row-key="id"
          :scroll="{ x: 'max-content' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'status'">
              <a-tag :color="orderStatusColor(record.status)">{{ orderStatusLabel(record.status) }}</a-tag>
            </template>
            <template v-else-if="column.key === 'price'">¥{{ record.price }}</template>
            <template v-else-if="column.key === 'refundAmount'">
              <span v-if="record.refundAmount > 0" class="tabular-nums text-warning">¥{{ record.refundAmount }}</span>
              <span v-else class="text-gray-400">-</span>
            </template>
            <template v-else-if="column.key === 'createdAt'">
              <DatetimeViewer :value="record.createdAt" />
            </template>
          </template>
        </a-table>
      </a-tab-pane>
      <a-tab-pane key="logs" :tab="t('shell.account.order.tabLogs')">
        <EmptyState v-if="logEmpty" :description="t('shell.account.order.emptyLog')" />
        <a-table
          v-else
          :columns="logColumns"
          :data-source="logs"
          :loading="loadingLogs"
          :pagination="false"
          row-key="id"
          :scroll="{ x: 'max-content' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'change'">
              <span :class="record.change >= 0 ? 'text-success' : 'text-error'">
                {{ record.change >= 0 ? '+' : '' }}{{ record.change }}
              </span>
            </template>
            <template v-else-if="column.key === 'createdAt'">
              <DatetimeViewer :value="record.createdAt" />
            </template>
          </template>
        </a-table>
      </a-tab-pane>
    </a-tabs>
  </a-modal>
</template>
